import { useLayout, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import Image from '@dustin-web/microsite-components/src/image'
import { useEffect, useRef } from 'react'
import { GetBrandNavigationQuery } from '@dustin-web/microsite-data/src/graphql/content/generated/graphql-types'
import { GET_BRAND_NAVIGATION } from '@dustin-web/microsite-data/src/graphql/content/get-brand-navigation.graphql'
import { useHeaderContext } from './header-context'
import { stripTrailingSlash } from '@dustin-web/microsite-components/src/utils/urls'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import classNames from 'classnames'
import { Skeleton } from '@dustin-web/microsite-components/src/skeleton'

type Props = {
  showMobileBrands?: boolean
}

type Brand = {
  name: string
  url: string
  logo?: { url: string; relativeUrl: string; height: number } | null
}

const BrandMenu = ({ showMobileBrands = false }: Props) => {
  const { desktop } = useHeaderContext()
  const { responsive } = useLayout()
  const brandListRef = useRef<HTMLDivElement>(null)
  const isActive = desktop.activeMegaMenu === 'brand' || showMobileBrands

  const { translate } = useTranslations()
  const {
    data,
    error,
    isValidating: loading,
  } = useGraphql<GetBrandNavigationQuery>(GET_BRAND_NAVIGATION, {
    context: {
      skip: !isActive,
    },
  })

  useEffect(() => {
    if (desktop.activeMegaMenu === desktop.focusMegaMenu) {
      brandListRef.current?.focus()
    }
  }, [desktop])

  if (error) {
    throw error
  }

  const brandNavigation = data?.brandNavigation

  const allBrandsLink = brandNavigation?.allBrandsUrl

  return !responsive.isSmallScreen ? (
    <div className="u-py-24">
      <div ref={brandListRef} tabIndex={0} className="row">
        {loading
          ? [...Array(48)].map((__, key) => (
              <div key={key} className="col-1 u-mb-24">
                <Skeleton withLogo width={66} height={33} />
              </div>
            ))
          : brandNavigation?.brands.map((brand: Brand, key: number) => (
              <div className="c-header__brand-logo col-1" key={brand.url + key}>
                <a href={stripTrailingSlash(brand.url)}>
                  {brand.logo ? (
                    <Image
                      height={40}
                      width={80}
                      src={brand.logo?.relativeUrl?.replace(/^\//, '') || ''}
                      alt={brand.name}
                      title={brand.name}
                      quality={100}
                    />
                  ) : (
                    brand.name
                  )}
                </a>
              </div>
            ))}
      </div>
      <div className="d-flex justify-content-between u-py-16 u-px-16">
        {'#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(character => (
          <a
            key={character}
            className="a-stripped d-inline-block u-fs-16"
            href={stripTrailingSlash(`${allBrandsLink ?? ''}#${character}`)}>
            {character}
          </a>
        ))}
        <a href={allBrandsLink}>{translate('BrandMenu_ShowAllBrands')}</a>
      </div>
    </div>
  ) : (
    <ul
      className={classNames('c-mobile-header-menu__primary-list ul-stripped', {
        'c-mobile-header-menu__primary-list--upcoming': !showMobileBrands,
      })}>
      <li>
        <a href={brandNavigation?.allBrandsUrl} className="c-mobile-header-menu__primary-action">
          {translate('BrandMenu_ShowAllBrands')}
        </a>
      </li>
      {brandNavigation?.brands.map((brand: Brand, key: number) => (
        <li key={brand.url + key}>
          <a href={brand.url} className="c-mobile-header-menu__primary-action">
            {brand.name}
          </a>
        </li>
      ))}
    </ul>
  )
}

export default BrandMenu
